import { Mail, Reply } from 'lucide-react';

import { ToggleGroup, ToggleGroupItem } from 'components/ToggleGroup';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { EmailMode } from 'types/EmailMode';
import { cn } from 'utils/shadcn';

interface EmailModeToggleProps {
  value: EmailMode;
  onValueChange: (value: EmailMode) => void;
  canReplyInThread?: boolean;
}

const EmailModeToggle = ({
  value,
  onValueChange,
  canReplyInThread = false,
}: EmailModeToggleProps) => {
  return (
    <Flex justify='between' gap='sm' align='center' className='mb-4'>
      <TooltipProvider>
        <ToggleGroup
          type='single'
          value={value}
          onValueChange={(newValue: string) => {
            // Only call onValueChange if we have a valid value
            // ToggleGroup can pass empty string on certain interactions
            if (
              newValue &&
              Object.values(EmailMode).includes(newValue as EmailMode)
            ) {
              onValueChange(newValue as EmailMode);
            }
          }}
          className='relative bg-neutral-100 border border-neutral-300 rounded-md p-1 grid w-full grid-cols-2 gap-0 m-0'
        >
          {/* Sliding background */}
          <div
            className={cn(
              'absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-md bg-brand-main transition-all duration-200 ease-in-out',
              {
                'left-1': value === EmailMode.New,
                'left-[calc(50%+1px)]': value === EmailMode.Reply,
              }
            )}
          />

          <ToggleGroupItem
            value={EmailMode.New}
            aria-label='New Email'
            className='relative z-10 flex-1 bg-transparent border-0 shadow-none data-[state=on]:text-white data-[state=on]:[&>svg]:stroke-white! data-[state=on]:shadow-sm data-[state=off]:text-neutral-600 data-[state=off]:[&>svg]:stroke-neutral-600! flex items-center justify-center gap-2'
          >
            <Mail size={20} aria-hidden='true' focusable='false' />
            New Email
          </ToggleGroupItem>

          <div className='relative w-full h-full flex items-center justify-center'>
            <ToggleGroupItem
              value={EmailMode.Reply}
              aria-label='Reply to thread'
              disabled={!canReplyInThread}
              className='relative z-10 w-full bg-transparent border-0 shadow-none data-[state=on]:text-white data-[state=on]:[&>svg]:stroke-white! data-[state=on]:shadow-sm data-[state=off]:text-neutral-600 data-[state=off]:[&>svg]:stroke-neutral-600! flex items-center justify-center gap-2 pl-1'
            >
              <Reply size={20} aria-hidden='true' focusable='false' />
              Reply
            </ToggleGroupItem>
            {/* Tooltip for disabled reply to thread option */}
            {!canReplyInThread && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='absolute inset-0 z-20 cursor-not-allowed' />
                </TooltipTrigger>
                <TooltipContent className='max-w-60 mr-2 text-xs'>
                  Reply to thread is only available when the Drumkit Sidebar is
                  open on an email thread.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </ToggleGroup>
      </TooltipProvider>
    </Flex>
  );
};

export default EmailModeToggle;
